/** @graphql agg **/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { graphql } from 'react-relay';

import { cfetch } from '@confluence/network';
import { expVal } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import {
	EXTERNAL_PRODUCT_CONNECTION,
	type AuthenticationStatus,
	type ExternalProductConnection,
} from '../__types__/RemoteObject.ts';

import type {
	use3PAuthenticationQuery$data,
	use3PAuthenticationQuery$variables,
} from './__generated__/use3PAuthenticationQuery.graphql.ts';

export const SearchThirdPartyAuthConfigurationQuery = graphql`
	query use3PAuthenticationQuery($cloudId: ID!) {
		search {
			configuration(cloudId: $cloudId) {
				thirdPartyProducts {
					name
					isUserOAuthed
					outboundAuthUrl
					providerId
				}
			}
		}
	}
`;

export type ThirdPartyAuthenticationResult = {
	authenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	unauthenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	isLoading: boolean;
	refetch: () => Promise<void>;
	markOptimisticallyAuthenticated: (product: ExternalProductConnection) => void;
};

// name` values from `search.configuration.thirdPartyProducts
enum SEARCH_THIRD_PARTY_CONNECTOR_NAME {
	GOOGLE_DRIVE = 'google-drive-connector',
	MICROSOFT_TEAMS = 'microsoft-teams-connector',
}

type SearchThirdPartyProductRow = {
	name: string;
	isUserOAuthed: boolean;
	outboundAuthUrl: string;
	providerId?: string | null;
};

type SearchThirdPartyAuthConfigurationResponse = {
	data?: {
		search?: {
			configuration?: {
				thirdPartyProducts?: SearchThirdPartyProductRow[];
			} | null;
		} | null;
	};
	errors?: ReadonlyArray<{ message?: string }>;
};

const SEARCH_THIRD_PARTY_AUTH_CONFIGURATION_OPERATION = 'SearchThirdPartyAuthConfiguration';

const SEARCH_THIRD_PARTY_AUTH_CONFIGURATION_QUERY = `
query ($cloudId: ID!) {
	search {
		configuration(cloudId: $cloudId) {
			thirdPartyProducts {
				name
				isUserOAuthed
				outboundAuthUrl
				providerId
			}
		}
	}
}
`.trim();

const filterAuthenticationStatus = (
	merged: Partial<Record<ExternalProductConnection, AuthenticationStatus>>,
): Pick<ThirdPartyAuthenticationResult, 'authenticatedServices' | 'unauthenticatedServices'> => {
	const authenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>> =
		{};
	const unauthenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>> =
		{};

	(Object.entries(merged) as [ExternalProductConnection, AuthenticationStatus][]).forEach(
		([product, status]) => {
			if (status.isAuthenticated) {
				authenticatedServices[product] = status;
			} else {
				unauthenticatedServices[product] = status;
			}
		},
	);

	return { authenticatedServices, unauthenticatedServices };
};

async function fetchSearchThirdPartyProducts(
	cloudId: string,
): Promise<{ products: SearchThirdPartyProductRow[] } | { error: Error }> {
	try {
		const response = await cfetch(
			`/gateway/api/graphql?q=${SEARCH_THIRD_PARTY_AUTH_CONFIGURATION_OPERATION}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'atl-attribution': JSON.stringify({
						product: 'Confluence Cloud',
						service: 'confluence-frontend',
					}),
					...(window['GLOBAL_AGG_REGION_STEERING_HINT']
						? { 'Atl-Edge-Region-Hint': window['GLOBAL_AGG_REGION_STEERING_HINT'] }
						: {}),
				},
				body: JSON.stringify({
					query: SEARCH_THIRD_PARTY_AUTH_CONFIGURATION_QUERY,
					variables: { cloudId },
					operationName: SEARCH_THIRD_PARTY_AUTH_CONFIGURATION_OPERATION,
				}),
			},
		);

		const json = (await response.json()) as SearchThirdPartyAuthConfigurationResponse;

		if (json.errors?.length) {
			return {
				error: new Error(json.errors.map((e) => e.message ?? 'GraphQL error').join('; ')),
			};
		}

		const products = json.data?.search?.configuration?.thirdPartyProducts ?? [];
		return { products };
	} catch (e) {
		return {
			error: e instanceof Error ? e : new Error('Failed to fetch search third-party configuration'),
		};
	}
}

function authStatusForSearchConnector(
	products: SearchThirdPartyProductRow[],
	connectorName: string,
	fetchError?: Error,
): AuthenticationStatus {
	const outboundAuthUrl = outboundAuthUrlForConnector(products, connectorName);
	if (fetchError) {
		return { isAuthenticated: false, error: fetchError, outboundAuthUrl };
	}

	const matches = products.filter((p) => p.name === connectorName);
	if (matches.length === 0) {
		return { isAuthenticated: false, outboundAuthUrl };
	}

	return {
		isAuthenticated: matches.some((p) => p.isUserOAuthed),
		outboundAuthUrl,
	};
}

function outboundAuthUrlForConnector(
	products: SearchThirdPartyProductRow[],
	connectorName: string,
): string | undefined {
	const match = products.find((p) => p.name === connectorName);
	return match?.outboundAuthUrl;
}

type UseFetch3PAuthenticationsProps = {
	products: ExternalProductConnection[];
	cloudId: string;
};

export const useFetch3PAuthentications = ({
	products,
	cloudId,
}: UseFetch3PAuthenticationsProps): ThirdPartyAuthenticationResult => {
	const needsSearchProducts =
		products.includes(EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS) ||
		products.includes(EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE);

	// Apollo path — always call the hook, but skip when experiment is off or search products not needed
	const {
		data: apolloAuthData,
		loading: apolloAuthLoading,
		refetch: apolloAuthRefetch,
	} = useQueryInterop<use3PAuthenticationQuery$data, use3PAuthenticationQuery$variables>(
		SearchThirdPartyAuthConfigurationQuery,
		{
			variables: { cloudId },
			skip:
				!expVal('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', false) ||
				!needsSearchProducts,
			interopApolloAggClient: true,
		},
	);

	// cfetch path state — used when experiment is off
	const [mergedByProduct, setMergedByProduct] = useState<Partial<
		Record<ExternalProductConnection, AuthenticationStatus>
	> | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [optimisticallyAuthenticatedProducts, setOptimisticallyAuthenticatedProducts] = useState<
		Set<ExternalProductConnection>
	>(new Set());

	const markOptimisticallyAuthenticated = useCallback((product: ExternalProductConnection) => {
		setOptimisticallyAuthenticatedProducts((prev) => {
			const next = new Set(prev);
			next.add(product);
			return next;
		});
	}, []);

	const productsKey = products.join(',');
	const stableProductsRef = useRef(products);
	if (stableProductsRef.current.join(',') !== productsKey) {
		stableProductsRef.current = products;
	}

	const refetch = useCallback(async () => {
		// Apollo path handles data reactively — skip imperative fetch
		if (expVal('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', false)) {
			return;
		}

		const products = stableProductsRef.current;

		if (products.length === 0) {
			setMergedByProduct(null);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);

		try {
			const merged: Partial<Record<ExternalProductConnection, AuthenticationStatus>> = {};

			let searchProducts: SearchThirdPartyProductRow[] = [];
			let searchFetchError: Error | undefined;

			if (
				products.includes(EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS) ||
				products.includes(EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE)
			) {
				const searchResult = await fetchSearchThirdPartyProducts(cloudId);
				if ('error' in searchResult) {
					searchFetchError = searchResult.error;
				} else {
					searchProducts = searchResult.products;
				}
			}

			for (const product of products) {
				switch (product) {
					case EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS:
						merged[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS] = authStatusForSearchConnector(
							searchProducts,
							SEARCH_THIRD_PARTY_CONNECTOR_NAME.MICROSOFT_TEAMS,
							searchFetchError,
						);
						break;
					case EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE:
						merged[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE] = authStatusForSearchConnector(
							searchProducts,
							SEARCH_THIRD_PARTY_CONNECTOR_NAME.GOOGLE_DRIVE,
							searchFetchError,
						);
						break;
				}
			}

			setMergedByProduct(merged);
		} finally {
			setIsLoading(false);
		}
	}, [cloudId]);

	useEffect(() => {
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		void refetch();
	}, [refetch]);

	// Apollo-derived merged state
	const apolloMergedByProduct = useMemo<Partial<
		Record<ExternalProductConnection, AuthenticationStatus>
	> | null>(() => {
		if (!expVal('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', false)) {
			return null;
		}

		const merged: Partial<Record<ExternalProductConnection, AuthenticationStatus>> = {};
		const searchProductRows: SearchThirdPartyProductRow[] =
			(apolloAuthData?.search?.configuration?.thirdPartyProducts as
				| SearchThirdPartyProductRow[]
				| undefined) ?? [];

		const currentProducts = stableProductsRef.current;
		for (const product of currentProducts) {
			switch (product) {
				case EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS:
					merged[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS] = authStatusForSearchConnector(
						searchProductRows,
						SEARCH_THIRD_PARTY_CONNECTOR_NAME.MICROSOFT_TEAMS,
					);
					break;
				case EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE:
					merged[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE] = authStatusForSearchConnector(
						searchProductRows,
						SEARCH_THIRD_PARTY_CONNECTOR_NAME.GOOGLE_DRIVE,
					);
					break;
				// Loom is not fetched via the AGG query — keep it empty in Apollo path
			}
		}

		return merged;
	}, [apolloAuthData]);

	const activeMerged = expVal(
		'confluence_autonomous_space_curation_ssr_streaming',
		'isEnabled',
		false,
	)
		? apolloMergedByProduct
		: mergedByProduct;
	const activeIsLoading = expVal(
		'confluence_autonomous_space_curation_ssr_streaming',
		'isEnabled',
		false,
	)
		? apolloAuthLoading
		: isLoading;

	const { authenticatedServices, unauthenticatedServices } = useMemo(() => {
		if (activeMerged == null) {
			return { authenticatedServices: {}, unauthenticatedServices: {} };
		}

		const { authenticatedServices: authed, unauthenticatedServices: unauthed } =
			filterAuthenticationStatus(activeMerged);

		const filteredUnauthed: Partial<Record<ExternalProductConnection, AuthenticationStatus>> = {};
		(Object.entries(unauthed) as [ExternalProductConnection, AuthenticationStatus][]).forEach(
			([product, status]) => {
				if (!optimisticallyAuthenticatedProducts.has(product)) {
					filteredUnauthed[product] = status;
				}
			},
		);

		return { authenticatedServices: authed, unauthenticatedServices: filteredUnauthed };
	}, [activeMerged, optimisticallyAuthenticatedProducts]);

	return {
		authenticatedServices,
		unauthenticatedServices,
		isLoading: activeIsLoading,
		refetch: expVal('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', false)
			? async () => {
					await apolloAuthRefetch();
				}
			: refetch,
		markOptimisticallyAuthenticated,
	};
};
